import { StyleSheet } from '@react-pdf/renderer';
import themeCss from '../../index.css?raw';

export const A5_LANDSCAPE: [number, number] = [(210 * 72) / 25.4, (148 * 72) / 25.4];

/** Figma content is 794px wide (210mm at 96dpi) — ISO A5 landscape. */
const FIGMA_PX_TO_PT = 72 / 96;
export const px = (figmaPx: number): number => figmaPx * FIGMA_PX_TO_PT;

/** react-pdf `lineHeight` is a font-size multiplier, not an absolute length. */
const lh = (lineHeightPx: number, fontSizePx: number): number => lineHeightPx / fontSizePx;

const THEME_COLOR_DECLARATION = /--color-([a-z0-9-]+):\s*(#[0-9a-fA-F]{3,8})\b/g;

const themeColors = new Map<string, string>(
  [...themeCss.matchAll(THEME_COLOR_DECLARATION)].map(([, token, hex]) => [token, hex])
);

export const themeColor = (token: string): string => {
  const value = themeColors.get(token);
  if (!value) {
    throw new Error(`Missing --color-${token} in index.css`);
  }
  return value;
};

export const PDF_FONTS = {
  inter: 'Inter',
  poppins: 'Poppins',
} as const;

export const PDF_STYLES = StyleSheet.create({
  page: {
    backgroundColor: themeColor('white'),
    flexDirection: 'column',
  },
  topBar: {
    height: px(5),
    backgroundColor: themeColor('teal-500'),
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  bottomBar: {
    height: px(4),
    backgroundColor: themeColor('teal-500'),
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: px(48),
    paddingVertical: px(12),
    flexGrow: 0,
    flexShrink: 0,
  },
  logoWrap: {
    width: px(124),
    height: px(56),
    flexGrow: 0,
    flexShrink: 0,
  },
  logo: {
    width: px(124),
    height: px(56),
    objectFit: 'contain',
    objectPosition: 'left center',
  },
  headerRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  receiptTitle: {
    fontFamily: PDF_FONTS.poppins,
    fontWeight: 400,
    fontSize: px(14),
    lineHeight: lh(18, 14),
    color: themeColor('denim-500'),
    textAlign: 'right',
  },
  receiptSubtitle: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('gray-400'),
    textAlign: 'right',
  },
  dateTimeWrap: {
    paddingHorizontal: px(48),
    flexGrow: 0,
    flexShrink: 0,
  },
  dateTimeBar: {
    height: px(56),
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: px(1),
    borderColor: themeColor('teal-500'),
    borderRadius: px(10),
    paddingHorizontal: px(33),
    paddingVertical: px(13),
    flexGrow: 0,
    flexShrink: 0,
  },
  dateTimeColumn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: px(10),
  },
  dateTimeText: {
    flexDirection: 'column',
  },
  dateTimeLabel: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(10),
    lineHeight: lh(16, 10),
    color: themeColor('denim-500'),
  },
  dateTimeValue: {
    fontFamily: PDF_FONTS.poppins,
    fontWeight: 500,
    fontSize: px(14),
    lineHeight: lh(18, 14),
    color: themeColor('denim-500'),
    marginTop: px(2),
  },
  columnsWrap: {
    paddingHorizontal: px(48),
    paddingTop: px(16),
    paddingBottom: px(24),
    gap: px(24),
    flexGrow: 0,
    flexShrink: 0,
  },
  columns: {
    height: px(288),
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: px(24),
  },
  detailsColumn: {
    flex: 1,
  },
  patientColumn: {
    flex: 1,
    gap: px(16),
  },
  section: {
    borderWidth: px(1),
    borderColor: themeColor('plum-200'),
    borderRadius: px(10),
    paddingHorizontal: px(18),
    paddingVertical: px(14),
    gap: px(12),
  },
  sectionFill: {
    flex: 1,
  },
  sectionTitle: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 500,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('teal-600'),
  },
  sectionContent: {
    gap: px(12),
  },
  field: {
    gap: px(2),
  },
  fieldRow: {
    flexDirection: 'row',
    gap: px(16),
  },
  fieldHalf: {
    flex: 1,
    gap: px(2),
  },
  fieldLabel: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('gray-400'),
  },
  fieldValue: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('denim-500'),
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: px(8),
  },
  iconRowText: {
    flex: 1,
  },
  iconRowTitle: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(18, 12),
    color: themeColor('denim-800'),
  },
  iconRowDescription: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('gray-500'),
  },
  consentSection: {
    flex: 1,
    minHeight: px(90),
    borderWidth: px(1),
    borderColor: themeColor('teal-300'),
    borderRadius: px(10),
    backgroundColor: themeColor('mint-100'),
    paddingHorizontal: px(18),
    paddingVertical: px(14),
  },
  consentTitle: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 500,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('teal-600'),
  },
  consentStatement: {
    marginTop: px(1),
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('denim-500'),
  },
  consentSignatureRow: {
    marginTop: px(2),
    flexDirection: 'row',
    alignItems: 'center',
    gap: px(4),
  },
  consentMutedText: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(12),
    lineHeight: lh(16, 12),
    color: themeColor('gray-400'),
  },
  consentSignatureLine: {
    flex: 1,
    height: px(10),
    borderBottomWidth: px(1),
    borderBottomColor: themeColor('gray-400'),
  },
  consentDateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: px(4),
  },
  footer: {
    paddingHorizontal: px(48),
    flexGrow: 0,
    flexShrink: 0,
  },
  footerMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: px(3),
  },
  footerMuted: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(10),
    lineHeight: lh(16, 10),
    color: themeColor('gray-400'),
  },
  footerDate: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(10),
    lineHeight: lh(16, 10),
    color: themeColor('denim-500'),
  },
  footerLegalWrap: {
    marginTop: px(12),
    borderTopWidth: px(1),
    borderTopColor: themeColor('plum-200'),
    paddingTop: px(11),
  },
  footerLegal: {
    fontFamily: PDF_FONTS.inter,
    fontWeight: 400,
    fontSize: px(9),
    lineHeight: lh(16, 9),
    color: themeColor('gray-400'),
    textAlign: 'center',
  },
  bottomBarWrap: {
    paddingTop: px(14),
    flexGrow: 0,
    flexShrink: 0,
  },
});
